import React from 'react';
import styled from 'styled-components';

const ResourcesContainer = styled.div`
  padding: 2rem 0;
`;

const PageTitle = styled.h1`
  color: var(--dark);
  margin-bottom: 1.5rem;
  font-family: 'Montserrat', sans-serif;
`;

const SectionTitle = styled.h2`
  color: var(--dark);
  margin: 2rem 0 1rem;
  font-family: 'Montserrat', sans-serif;
  font-size: 1.5rem;
`;

const ResourceSection = styled.div`
  margin-bottom: 3rem;
`;

const ResourceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1.5rem;
  
  @media (min-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ResourceCard = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }
`;

const ResourceHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  background-color: ${props => props.bgColor || 'var(--light)'};
`;

const ResourceTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  color: white;
  text-align: center;
`;

const ResourceContent = styled.div`
  padding: 1.5rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  
  p {
    margin-bottom: 1rem;
    color: var(--gray);
    flex-grow: 1;
  }
`;

const ResourceButton = styled.a`
  display: inline-block;
  background-color: ${props => props.primary ? 'var(--primary)' : 'transparent'};
  color: ${props => props.primary ? 'white' : 'var(--primary)'};
  border: 1px solid var(--primary);
  padding: 0.5rem 1rem;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 500;
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.primary ? 'var(--primary-dark)' : 'rgba(233, 64, 87, 0.05)'};
    transform: translateY(-2px);
  }
`;

const EmergencyCard = styled.div`
  background-color: ${props => props.bgColor || 'var(--light)'};
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border-left: 4px solid ${props => props.borderColor || 'var(--primary)'};
`;

const EmergencyTitle = styled.h3`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  color: ${props => props.textColor || 'var(--dark)'};
  
  .icon {
    margin-right: 0.75rem;
  }
`;

const EmergencyContact = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .icon {
    width: 24px;
    margin-right: 0.75rem;
    color: var(--primary);
  }
  
  .contact-info {
    flex-grow: 1;
  }
  
  .contact-name {
    font-weight: 500;
    color: var(--dark);
  }
  
  .contact-number {
    color: var(--primary);
    font-weight: 700;
  }
  
  .contact-desc {
    color: var(--gray);
    font-size: 0.9rem;
  }
`;

const ResourcesPage = () => {
  const trustedOrganizations = [
    {
      name: 'Red Nose Australia',
      description: "A leading resource on safe sleep and SUDI prevention in Australia, providing evidence-based guidelines for infant sleep safety that we reference in our plans.",
      url: 'https://rednose.org.au/section/safe-sleeping',
      bgColor: '#7ECBB9'
    },
    {
      name: 'Triple P (Positive Parenting Program)',
      description: "Evidence-based parenting program offering strategies and support for raising confident, healthy children and building stronger family relationships. We reference their guidelines in our approach.",
      url: 'https://www.triplep-parenting.net.au/',
      bgColor: '#7ECBB9'
    },
    {
      name: 'Raising Children Network',
      description: "Comprehensive resource providing trusted information on child development, behavior, health and nutrition from pregnancy to teenage years. We incorporate their research in our sleep guidance.",
      url: 'https://raisingchildren.net.au/',
      bgColor: '#7ECBB9'
    },
    {
      name: 'Tresillian',
      description: "Australia's largest early parenting organization providing support services and advice for families with young children. Our plans reference their published guidelines.",
      url: 'https://www.tresillian.org.au/',
      bgColor: '#7ECBB9'
    },
    {
      name: 'Australian Breastfeeding Association',
      description: "Australia's leading authority on breastfeeding, providing education, support and resources for breastfeeding mothers.",
      url: 'https://www.breastfeeding.asn.au/',
      bgColor: '#7ECBB9'
    },
    {
      name: 'Sleep Health Foundation',
      description: "Leading advocate for healthy sleep, providing evidence-based information about sleep health for all Australians.",
      url: 'https://www.sleephealthfoundation.org.au/',
      bgColor: '#7ECBB9'
    }
  ];
  
  const emergencyResources = [
    {
      name: 'Emergency Services',
      number: '000',
      description: 'For life-threatening emergencies',
      icon: 'bi-telephone-fill'
    },
    {
      name: 'Healthdirect Australia',
      number: '1800 022 222',
      description: '24/7 health advice line with registered nurses',
      icon: 'bi-heart-pulse-fill'
    },
    {
      name: 'Poison Information Centre',
      number: '13 11 26',
      description: 'For advice about poisoning or suspected poisoning',
      icon: 'bi-exclamation-triangle-fill'
    },
    {
      name: 'R U OK?',
      number: '1800 275 869',
      description: 'Support for people struggling with life',
      icon: 'bi-question-circle-fill'
    },
    {
      name: 'Beyond Blue',
      number: '1300 22 4636',
      description: '24/7 support for anxiety, depression and suicide prevention',
      icon: 'bi-brightness-high-fill'
    }
  ];
  
  const additionalResources = [
    {
      name: 'Sleep Training Methods',
      description: 'Learn about different evidence-based sleep training approaches and find what works best for your family.',
      icon: 'bi-book-fill',
      url: 'https://raisingchildren.net.au/babies/sleep/solving-sleep-problems/sleep-training',
      bgColor: '#7ECBB9'
    },
    {
      name: 'Sleep Environment Guide',
      description: 'Create the optimal sleep environment for your child with this comprehensive guide to room setup, temperature, and more.',
      icon: 'bi-house-heart-fill',
      url: 'https://rednose.org.au/article/creating-a-safe-sleep-environment',
      bgColor: '#7ECBB9'
    },
    {
      name: 'Age-Specific Sleep Needs',
      description: 'Understand how much sleep your child needs at different ages and stages of development.',
      icon: 'bi-calendar-week-fill',
      url: 'https://www.sleephealthfoundation.org.au/how-much-sleep-do-you-need.html',
      bgColor: '#7ECBB9'
    }
  ];
  
  return (
    <div className="container">
      <ResourcesContainer>
        <PageTitle>Resources</PageTitle>
        <p className="lead mb-4">
          Access trusted information and support from Australia's leading parenting and child health organizations.
        </p>
        
        <ResourceSection>
          <SectionTitle>Trusted Organizations</SectionTitle>
          <ResourceGrid>
            {trustedOrganizations.map((org, index) => (
              <ResourceCard key={index}>
                <ResourceHeader bgColor={org.bgColor}>
                  <ResourceTitle>{org.name}</ResourceTitle>
                </ResourceHeader>
                <ResourceContent>
                  <p>{org.description}</p>
                  <ResourceButton href={org.url} target="_blank" rel="noopener noreferrer" primary>
                    Visit Website
                  </ResourceButton>
                </ResourceContent>
              </ResourceCard>
            ))}
          </ResourceGrid>
        </ResourceSection>
        
        <ResourceSection>
          <SectionTitle>Emergency Resources</SectionTitle>
          <EmergencyCard bgColor="#FFF4F5" borderColor="#E94057">
            <EmergencyTitle textColor="#E94057">
              <i className="bi bi-exclamation-triangle-fill icon"></i>
              Emergency Contacts
            </EmergencyTitle>
            <p className="mb-4">
              If you or your child are experiencing a medical emergency, please call 000 immediately.
              The following resources are available for various types of support:
            </p>
            
            {emergencyResources.map((resource, index) => (
              <EmergencyContact key={index}>
                <div className="icon">
                  <i className={resource.icon}></i>
                </div>
                <div className="contact-info">
                  <div className="contact-name">{resource.name}</div>
                  <div className="contact-desc">{resource.description}</div>
                </div>
                <div className="contact-number">{resource.number}</div>
              </EmergencyContact>
            ))}
          </EmergencyCard>
        </ResourceSection>
        
        <ResourceSection>
          <SectionTitle>Additional Resources</SectionTitle>
          <ResourceGrid>
            {additionalResources.map((resource, index) => (
              <ResourceCard key={index}>
                <ResourceHeader bgColor={resource.bgColor}>
                  <ResourceTitle>{resource.name}</ResourceTitle>
                </ResourceHeader>
                <ResourceContent>
                  <p>{resource.description}</p>
                  <ResourceButton href={resource.url} target="_blank" rel="noopener noreferrer" primary>
                    Learn More
                  </ResourceButton>
                </ResourceContent>
              </ResourceCard>
            ))}
          </ResourceGrid>
        </ResourceSection>
      </ResourcesContainer>
    </div>
  );
};

export default ResourcesPage;
